import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  IconButton,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const SessionForm = ({ open, onClose, onSubmit, initialData, title = 'Add Session', loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    is_active: false,
    description: '',
    academic_year: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        start_date: initialData.start_date || '',
        end_date: initialData.end_date || '',
        is_active: initialData.is_active || false,
        description: initialData.description || '',
        academic_year: initialData.academic_year || '',
      });
    } else {
      setFormData({
        name: '',
        start_date: '',
        end_date: '',
        is_active: false,
        description: '',
        academic_year: '',
      });
    }
    setErrors({});
  }, [initialData, open]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Session name is required';
    }
    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }
    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }
    if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      if (end <= start) {
        newErrors.end_date = 'End date must be after start date';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = { ...formData };
    if (initialData?.id) payload.id = initialData.id;
    await onSubmit(payload);
  };

  const handleClose = () => {
    if (loading) return;
    setFormData({
      name: '',
      start_date: '',
      end_date: '',
      is_active: false,
      description: '',
      academic_year: '',
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={!!open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {title}
          <IconButton onClick={handleClose} size="small" disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ mt: 1 }}>
          <TextField
            fullWidth
            margin="normal"
            label="Session Name"
            value={formData.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            required
            disabled={loading}
            placeholder="e.g., 2024-2025 Academic Session"
            autoFocus
          />
          <TextField
            fullWidth
            margin="normal"
            label="Academic Year"
            value={formData.academic_year}
            onChange={handleChange('academic_year')}
            error={!!errors.academic_year}
            helperText={errors.academic_year || 'e.g., 2024-2025'}
            disabled={loading}
            placeholder="2024-2025"
          />
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Start Date"
              type="date"
              value={formData.start_date}
              onChange={handleChange('start_date')}
              error={!!errors.start_date}
              helperText={errors.start_date}
              required
              disabled={loading}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="End Date"
              type="date"
              value={formData.end_date}
              onChange={handleChange('end_date')}
              error={!!errors.end_date}
              helperText={errors.end_date}
              required
              disabled={loading}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <TextField
            fullWidth
            margin="normal"
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            error={!!errors.description}
            helperText={errors.description}
            disabled={loading}
            multiline
            rows={3}
            placeholder="Optional description or notes about this session"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={handleChange('is_active')}
                disabled={loading}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Set as Active Session
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Only one session can be active at a time
                </Typography>
              </Box>
            }
            sx={{ mt: 2 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {initialData?.id ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionForm;

